import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { organizationsApi } from "../api/organizations";

const orgKeys = {
  all: ["organizations"] as const,
  list: (params?: Record<string, unknown>) => [...orgKeys.all, "list", params] as const,
  detail: (id: string) => [...orgKeys.all, "detail", id] as const,
  members: (id: string) => [...orgKeys.all, "members", id] as const,
  roles: (id: string) => [...orgKeys.all, "roles", id] as const,
};

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useOrganizations(params?: { search?: string; first?: number; max?: number }) {
  return useQuery({
    queryKey: orgKeys.list(params as Record<string, unknown>),
    queryFn: () => organizationsApi.list(params),
    refetchInterval: 30_000,
  });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: orgKeys.detail(id),
    queryFn: () => organizationsApi.get(id),
    enabled: !!id,
  });
}

export function useOrgMembers(id: string) {
  return useQuery({
    queryKey: orgKeys.members(id),
    queryFn: () => organizationsApi.listMembers(id),
    enabled: !!id,
    refetchInterval: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: organizationsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export function useUpdateOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof organizationsApi.update>[1]) =>
      organizationsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.detail(id) });
      qc.invalidateQueries({ queryKey: orgKeys.list() });
    },
  });
}

export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: organizationsApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export function useAddOrgMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => organizationsApi.addMember(orgId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
    },
  });
}

export function useInviteToOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => organizationsApi.invite(orgId, email),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
    },
  });
}

export function useRemoveOrgMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => organizationsApi.removeMember(orgId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
    },
  });
}

export function useOrgRoles(orgId: string) {
  return useQuery({
    queryKey: orgKeys.roles(orgId),
    queryFn: () => organizationsApi.listRoles(orgId),
    enabled: !!orgId,
    staleTime: 60_000,
  });
}

export function useCreateOrgRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      organizationsApi.createRole(orgId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.roles(orgId) });
    },
  });
}

export function useDeleteOrgRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleName: string) => organizationsApi.deleteRole(orgId, roleName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.roles(orgId) });
    },
  });
}

export function useChangeMemberRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      grant,
      revoke,
    }: {
      userId: string;
      grant?: Array<{ id: string; name: string }>;
      revoke?: Array<{ id: string; name: string }>;
    }) => organizationsApi.changeMemberRole(orgId, userId, grant, revoke),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
    },
  });
}
